import { createQueryKeys } from "@lukemorales/query-key-factory";
import { get, PaginatedResponse } from "@/api/api";
import { LocationData } from "@/location/location";
import { useSuspenseQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { SortingState } from "@tanstack/react-table";
import { apiClient } from "@/api/client";
import { useSelectedOrganizationId } from "@/organization/api";
import { makeOptimisticHandlers } from "@/api/optimistic";

export type LocationTableFilters = {
  organization_id?: number | number[];
  include_inactive?: boolean;
};

export const locations = createQueryKeys('locations', {
  list: (
    {
      page = 1,
      pageSize = 10,
      sorting = [],
      filters = {},
    }: {
      page?: number;
      pageSize?: number;
      sorting?: SortingState;
      filters?: LocationTableFilters;
    }
  ) => ({
    queryKey: [page, pageSize, sorting, filters],
    queryFn: async () => {
      // Use old endpoint if multiple or no org IDs are passed
      const useOldEndpoint = Array.isArray(filters.organization_id) || !filters.organization_id;

      let url: string;
      if (useOldEndpoint) {
        url = `/locations/?page=${page}&page_size=${pageSize}`;
        if (Array.isArray(filters.organization_id)) {
          filters.organization_id.forEach((id: number) => {
            url += `&organization_id=${id}`;
          });
        }
      } else {
        // Use new nested endpoint for single org ID
        const orgId = filters.organization_id;
        url = `/organizations/${orgId}/locations/?page=${page}&page_size=${pageSize}`;
      }

      if (sorting.length) {
        url += `&ordering=${sorting[0]?.desc ? "-" : ""}${sorting[0]?.id}`;
      }

      if (filters.include_inactive) {
        url += `&include_inactive=true`;
      }

      return await get<PaginatedResponse<LocationData>>(url);
    }
  }),
  detail: (
    locationId: number,
    organizationId?: number,
  ) => ({
    queryKey: [locationId, organizationId],
    queryFn: async () => {
      const url = organizationId
        ? `/organizations/${organizationId}/locations/${locationId}/?include_inactive=true`
        : `locations/${locationId}/?include_inactive=true`;
      return await get<LocationData>(url);
    },
  }),
});

export const useLocations = (
  {
    page = 1,
    pageSize = 10,
    sorting = [],
    filters = {},
  }: {
    page?: number;
    pageSize?: number;
    sorting?: SortingState;
    filters?: LocationTableFilters,
  }
) => {
  return useSuspenseQuery(locations.list({ page, pageSize, sorting, filters }));
};

export const useLocation = (locationId: number) => {
  const selectedOrgId = useSelectedOrganizationId();
  return useSuspenseQuery(locations.detail(locationId, selectedOrgId));
};

export type CreateLocationInput = {
  name: string;
  address_line_1: string;
  address_line_2?: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
  organization: number;
  access_notes?: string;
  latitude?: number;
  longitude?: number;
};

export const useCreateLocation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateLocationInput) => {
      const url = `/organizations/${data.organization}/locations/`;
      const res = await apiClient.post(url, data);
      return res.data as LocationData;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: locations._def });
    },
  });
};

export type MatchByCoordinatesInput = {
  latitude: number;
  longitude: number;
};

export type MatchByCoordinatesResult = {
  matched_location: LocationData | null;
  match_distance_meters: number | null;
  reverse_geocode_address: {
    street: string;
    city: string;
    state: string;
    postal_code: string;
    country: string;
  } | null;
  reverse_geocode_display: string | null;
};

export const useRenameLocation = () => {
  const queryClient = useQueryClient();
  const selectedOrganizationId = useSelectedOrganizationId();

  return useMutation({
    mutationFn: async (data: { location_id: number; name: string }) => {
      const res = await apiClient.patch(
        `organizations/${selectedOrganizationId}/locations/${data.location_id}/`,
        { name: data.name }
      );
      return res.data as LocationData;
    },
    ...makeOptimisticHandlers<LocationData, { location_id: number; name: string }>({
      queryClient,
      rootDef: locations._def,
      detailDef: locations.detail._def,
      listDef: locations.list._def,
      getId: (vars) => vars.location_id,
      updater: (old, vars) => ({ ...old, name: vars.name }),
    }),
  });
};

export type UpdateLocationAddressInput = {
  location_id: number;
  address_line_1: string;
  address_line_2?: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
  latitude?: number;
  longitude?: number;
};

export const useUpdateLocationAddress = () => {
  const queryClient = useQueryClient();
  const selectedOrganizationId = useSelectedOrganizationId();

  return useMutation({
    mutationFn: async (data: UpdateLocationAddressInput) => {
      const { location_id, ...fields } = data;
      const res = await apiClient.patch(
        `organizations/${selectedOrganizationId}/locations/${location_id}/`,
        fields
      );
      return res.data as LocationData;
    },
    ...makeOptimisticHandlers<LocationData, UpdateLocationAddressInput>({
      queryClient,
      rootDef: locations._def,
      detailDef: locations.detail._def,
      listDef: locations.list._def,
      getId: (vars) => vars.location_id,
      updater: (old, vars) => ({
        ...old,
        address_line_1: vars.address_line_1,
        address_line_2: vars.address_line_2,
        city: vars.city,
        state: vars.state,
        postal_code: vars.postal_code,
        country: vars.country,
        ...(vars.latitude != null && { latitude: vars.latitude }),
        ...(vars.longitude != null && { longitude: vars.longitude }),
      }),
    }),
  });
};

export const useUpdateLocationActive = () => {
  const queryClient = useQueryClient();
  const selectedOrganizationId = useSelectedOrganizationId();

  return useMutation({
    mutationFn: async (data: { location_id: number; is_active: boolean }) => {
      const res = await apiClient.patch(
        `organizations/${selectedOrganizationId}/locations/${data.location_id}/?include_inactive=true`,
        { is_active: data.is_active }
      );
      return res.data as LocationData;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: locations._def });
    },
  });
};

export const useMatchLocationByCoordinates = () => {
  const queryClient = useQueryClient();
  const organizationId = useSelectedOrganizationId();

  return useMutation({
    mutationFn: async (data: MatchByCoordinatesInput) => {
      const res = await apiClient.post<MatchByCoordinatesResult>(
        `/organizations/${organizationId}/locations/match-by-coordinates/`,
        data
      );
      return res.data;
    },
    onSuccess: () => {
      // Invalidate locations cache to ensure UI is fresh
      queryClient.invalidateQueries({ queryKey: locations._def });
    },
  });
};

